import { Spine } from "@esotericsoftware/spine-pixi-v7";
import * as PIXI from "pixi.js";
import { Settings } from "../../../settings";
import { formatToCustomLocale } from "../../../utilities";
import { FONT_STYLES } from "../../../fontStyles";
import SpineAnimationData from "../../../utils/SpineAnimationData";

// Win text configuration - fine tune both global and per-line win text
export const WIN_TEXT_CONFIG = {
  // Font size as multiplier of SYMBOL_HEIGHT (higher = bigger text)
  GLOBAL_WIN_FONT_SIZE: 0.65, // Font size for "show all wins" phase
  LINE_WIN_FONT_SIZE: 0.55, // Font size for individual line cycling

  // Spine scale multiplier — enlarges the glow effect without affecting text size.
  // Text is counter-scaled by 1/multiplier to stay the same.
  SPINE_SCALE_MULTIPLIER: 2.0, // 1.0 = native, >1 = bigger glow

  // Text Y offset relative to bone position (positive = down, negative = up)
  TEXT_Y_OFFSET: 0,
};

/**
 * WinTextController - Unified controller for both global and line win text
 * Uses winline_text spine with txt bone for text attachment
 * Configurable font size allows it to serve both purposes
 */
class WinTextController {
  constructor(container, symbolWidth, symbolHeight, fontSizeMultiplier = 0.25) {
    this.container = container;
    this.symbolWidth = symbolWidth;
    this.symbolHeight = symbolHeight;
    this.fontSizeMultiplier = fontSizeMultiplier; // 0.25 for line wins, 0.35 for global wins

    // Spine instance
    this.winlineSpine = null;
    this.initialized = false;
    this.isCleanedUp = false;

    // Text container attached to spine bone
    this.textContainer = null;
    this.textBitmap = null;
    this.boneUpdateTicker = null;

    // Internal cache for performance optimization
    this._scaleCache = null;
    this._lastSymbolWidth = symbolWidth;
    this._lastSymbolHeight = symbolHeight;

    this.initializeSpine();
  }

  /**
   * Check if controller is ready
   */
  isReady() {
    return this.winlineSpine?.state && this.initialized && !this.isCleanedUp;
  }

  /**
   * Get the spine instance for event listening
   */
  getSpineInstance() {
    return this.winlineSpine;
  }

  /**
   * Initialize the winline_text spine
   */
  initializeSpine() {
    try {
      const assets = PIXI.Assets.cache;
      if (
        !assets.has("winline_text-skeleton") ||
        !assets.has("winline_text-atlas")
      ) {
        return;
      }

      const skeletonData = PIXI.Assets.get("winline_text-skeleton");
      const atlasData = PIXI.Assets.get("winline_text-atlas");

      if (!skeletonData || !atlasData) {
        return;
      }

      this.winlineSpine = Spine.from({
        skeleton: "winline_text-skeleton",
        atlas: "winline_text-atlas",
      });

      if (
        !this.winlineSpine ||
        !this.winlineSpine.skeleton ||
        !this.winlineSpine.state
      ) {
        return;
      }

      // Scale spine proportionally to current screen size
      this.applySpineScaling();

      // Position at center
      this.winlineSpine.x = 0;
      this.winlineSpine.y = 0;
      this.winlineSpine.zIndex = 20;
      this.winlineSpine.alpha = 0.001; // Invisible but stays in GPU pipeline

      this.container.addChild(this.winlineSpine);

      // Initialize text attached to bone
      this.initializeText();

      this.initialized = true;

      // Warm up spine to prevent first-use jitter
      this.warmUpSpine();
    } catch (error) {
      // Silent fail
    }
  }

  /**
   * Hide smoke effect by removing attachments from smoke-related slots
   * NOTE: Disabled — full spine animation now plays including all effects
   */
  hideSmokeEffect() {
    // No-op: full animation enabled
  }

  /**
   * Warm up spine by setting animation and updating transforms
   * This ensures spine is fully ready before first use (prevents jitter)
   */
  warmUpSpine() {
    if (!this.winlineSpine?.state) return;

    try {
      // Play "in" animation briefly to populate spine structure and GPU buffers
      this.winlineSpine.state.setAnimation(0, "in", false);
      this.winlineSpine.state.update(0.016);

      // Populate bone hierarchy for GPU
      try {
        this.winlineSpine.skeleton.updateWorldTransform();
      } catch (e) {
        // Physics not initialized — safe to continue
      }

      // Clear tracks and reset for clean first use
      // NOTE: Do NOT call setToSetupPose() — causes DarkSlotMesh memory leak in spine-pixi-v7
      this.winlineSpine.state.clearTracks();
      this.winlineSpine.state.clearListeners();
    } catch (error) {
      // Silent fail - warmup is optional optimization
    }
  }

  /**
   * Apply responsive scaling to match current screen size.
   * At design resolution (max width) the spine renders at native size (scale=1.0).
   * On smaller screens it shrinks proportionally, preserving aspect ratio.
   */
  applySpineScaling() {
    if (!this.winlineSpine) return;

    const nativeWidth = Settings.SYMBOL_WIDTH;
    const nativeHeight = Settings.SYMBOL_HEIGHT;
    const widthScale = this.symbolWidth / nativeWidth;
    const heightScale = this.symbolHeight / nativeHeight;
    const scaleRatio = Math.min(widthScale, heightScale);

    // Apply multiplier to enlarge glow — text is counter-scaled in updateTextFromBone
    const finalScale = scaleRatio * (WIN_TEXT_CONFIG.SPINE_SCALE_MULTIPLIER || 1);
    this.winlineSpine.scale.set(finalScale);
  }

  /**
   * Initialize text container and attach to spine bone
   */
  initializeText() {
    if (!this.winlineSpine || !this.winlineSpine.skeleton) {
      return;
    }

    // Verify bone exists — new spine uses "basic_win" bone
    const textBone = this.winlineSpine.skeleton.findBone("basic_win");
    if (!textBone) {
      return;
    }

    // Create text container as child of spine
    this.textContainer = new PIXI.Container();
    this.textContainer.name = "winline_text_attached_to_bone";
    this.textContainer.alpha = 0;
    this.textContainer.zIndex = 1000;

    this.winlineSpine.addChild(this.textContainer);
    this.winlineSpine.sortableChildren = true;

    const fontSize = Math.floor(
      Settings.SYMBOL_HEIGHT * this.fontSizeMultiplier,
    );

    const textStyle = new PIXI.TextStyle({
      ...FONT_STYLES.winText,
      fontSize: fontSize,
    });

    this.textBitmap = new PIXI.Text("", textStyle);

    this.textBitmap.anchor.set(0.5);
    this.textBitmap.x = 0;
    this.textBitmap.y = 0;
    this.textBitmap.alpha = 0.001;

    this.textContainer.addChild(this.textBitmap);
  }

  /**
   * Update text display with new value
   */
  updateTextDisplay(value) {
    if (!this.textBitmap) return;

    // Format value - numbers only, no currency symbol
    const formattedValue = formatToCustomLocale(Math.floor(value));

    // Update the text value
    this.textBitmap.text = formattedValue;

    // Auto-shrink if text overflows the spine's native width
    // Uses the skeleton bounding box as the max width constraint
    this.textBitmap.scale.set(1); // Reset before measuring
    const maxWidth = this.winlineSpine?.skeleton?.data?.width || 0;
    if (maxWidth > 0 && this.textBitmap.width > maxWidth) {
      const scale = maxWidth / this.textBitmap.width;
      this.textBitmap.scale.set(scale);
    }
  }

  /**
   * Sync text container transform with spine's txt_winline_value bone
   * Called every frame while spine is animating
   */
  updateTextFromBone() {
    if (
      !this.winlineSpine ||
      !this.textContainer ||
      !this.winlineSpine.skeleton
    ) {
      return;
    }

    // New spine uses "basic_win" bone
    const textBone = this.winlineSpine.skeleton.findBone("basic_win");
    if (!textBone) {
      return;
    }

    // Apply bone's world POSITION (needed for animated text tracking)
    // but use LOCAL scale to avoid double-scaling — textContainer already
    // inherits spine's own scale as a child. Using worldScale would apply
    // spine scale twice, crushing the text raster and causing pixelation.
    // Counter-scale by 1/SPINE_SCALE_MULTIPLIER so text stays at original size.
    const counterScale = 1 / (WIN_TEXT_CONFIG.SPINE_SCALE_MULTIPLIER || 1);
    this.textContainer.x = textBone.worldX;
    this.textContainer.y = textBone.worldY;
    this.textContainer.rotation =
      (textBone.getWorldRotationX() * Math.PI) / 180;
    this.textContainer.scale.set(
      textBone.scaleX * counterScale,
      textBone.scaleY * counterScale,
    );

    // Apply slot's color alpha to text container (for fade-in/out animation)
    // New spine uses "txt_slot_value_basic_win" slot
    const textSlot = this.winlineSpine.skeleton.findSlot("txt_slot_value_basic_win");
    if (textSlot && textSlot.color) {
      this.textContainer.alpha = textSlot.color.a;
    }
  }

  /**
   * Start syncing text with spine bone every frame
   */
  startBoneUpdateLoop() {
    if (!this.winlineSpine || !this.textContainer) return;

    if (!this.boneUpdateTicker) {
      this.boneUpdateTicker = () => {
        if (this.winlineSpine && this.winlineSpine.alpha > 0.01) {
          this.updateTextFromBone();
        }
      };
      PIXI.Ticker.shared.add(this.boneUpdateTicker);
    }
  }

  /**
   * Stop syncing text with spine bone
   */
  stopBoneUpdateLoop() {
    if (this.boneUpdateTicker) {
      PIXI.Ticker.shared.remove(this.boneUpdateTicker);
      this.boneUpdateTicker = null;
    }
  }

  /**
   * Show win amount with spine animation.
   * Plays "in" → "idle" (once) → "out". Caller controls lifecycle via hide().
   * @param {number} amount - Win amount to display
   * @param {Object} [options] - Optional config
   * @param {boolean} [options.skipIntro=false] - Skip "in" phase, play idle→out directly (super turbo)
   * @param {number} [options.targetDuration] - If set, auto-scales playback to fit in→idle→out in this many ms
   */
  showWinAmount(amount, { skipIntro = false, targetDuration } = {}) {
    if (!this.isReady()) return;

    // Cancel any pending out-animation resolve
    this._cancelPendingOut();

    // Step 1: Ensure invisible during setup (alpha-only, no visible toggle)
    this.winlineSpine.alpha = 0.001;

    // Step 2: Update text while invisible
    this.updateTextDisplay(amount);

    // Step 3: Reset spine animation state
    // NOTE: Do NOT call setToSetupPose() — causes DarkSlotMesh memory leak in spine-pixi-v7
    if (this.winlineSpine.state) {
      this.winlineSpine.state.clearTracks();
      this.winlineSpine.state.clearListeners();
      this.winlineSpine.state.timeScale = 1; // Reset from any previous speed-up
    }

    // Step 3b: Apply timeScale if targetDuration is set — speeds up the full
    // in→idle→out chain to fit within the requested duration.
    if (targetDuration && targetDuration > 0 && this.winlineSpine.state) {
      const nativeDuration = this._getAnimationChainDuration(skipIntro);
      if (nativeDuration > 0 && nativeDuration > targetDuration) {
        this.winlineSpine.state.timeScale = nativeDuration / targetDuration;
      }
    }

    // Step 3b: Reset text slot alpha — clearTracks() doesn't reset slot colors.
    // After "out" animation fades slot alpha to 0, it stays 0 unless "in" re-animates it.
    // When skipIntro skips "in", the stale alpha=0 makes text invisible.
    const textSlot = this.winlineSpine.skeleton.findSlot("txt_slot_value_basic_win");
    if (textSlot?.color) {
      textSlot.color.a = 1;
    }

    // Step 4: Chain animation sequence
    if (this.winlineSpine.state) {
      if (skipIntro) {
        // Super turbo: skip "in" fade — appear at full opacity immediately
        this.winlineSpine.state.setAnimation(0, "idle", false);
        this.winlineSpine.state.addAnimation(0, "out", false, 0);
      } else {
        // Normal: full cycle "in" → "idle" → "out"
        this.winlineSpine.state.setAnimation(0, "in", false);
        this.winlineSpine.state.addAnimation(0, "idle", false, 0);
        this.winlineSpine.state.addAnimation(0, "out", false, 0);
      }
      this.winlineSpine.state.apply(this.winlineSpine.skeleton);
      this.winlineSpine.state.update(0.016);
      this.winlineSpine.state.apply(this.winlineSpine.skeleton);
    }

    // Step 5: Start bone update loop (will run on next ticker frame)
    this.startBoneUpdateLoop();

    // Step 6: Reveal
    if (skipIntro) {
      // Super turbo: reveal SYNCHRONOUSLY — no RAF defer.
      // Prevents race where hide() from resetAllEffects cancels the pending RAF.
      this.updateTextFromBone();
      this.winlineSpine.alpha = 1;
      if (this.textBitmap) {
        this.textBitmap.alpha = 1;
      }
    } else {
      // Normal: reveal on NEXT frame after ticker has updated world transforms
      if (this._showRAF) cancelAnimationFrame(this._showRAF);
      this._showRAF = requestAnimationFrame(() => {
        this._showRAF = null;
        if (!this.winlineSpine || this.winlineSpine.destroyed) return;

        // Sync text with bone AFTER ticker has updated world transforms
        this.updateTextFromBone();

        // Make spine visible — spine's slot animation drives textContainer.alpha
        this.winlineSpine.alpha = 1;
        // Ensure text bitmap is renderable (slot alpha controls actual visibility)
        if (this.textBitmap) {
          this.textBitmap.alpha = 1;
        }
      });
    }
  }

  /**
   * Play the "out" animation for a smooth natural transition.
   * Returns a Promise that resolves when "out" finishes.
   * The spine auto-hides and state is fully cleared after "out" completes,
   * so the next showWinAmount() starts a fresh in→idle cycle.
   *
   * If hide() is called during "out", the promise resolves immediately
   * and the hard kill takes precedence.
   */
  playOut() {
    return new Promise((resolve) => {
      if (!this.isReady() || !this.winlineSpine?.state) {
        resolve();
        return;
      }

      // Store resolve so hide() can cancel it
      this._outResolve = resolve;

      // Clear current animation and listeners, then play "out"
      this.winlineSpine.state.clearListeners();
      this.winlineSpine.state.setAnimation(0, "out", false);

      // Listen for "out" animation complete
      this.winlineSpine.state.addListener({
        complete: (entry) => {
          if (entry.animation?.name === "out") {
            // "out" finished naturally — clean up and resolve
            this._finalizeOut();
            resolve();
            this._outResolve = null;
          }
        },
      });
    });
  }

  /**
   * Cancel any pending playOut() promise.
   * Called by hide() and showWinAmount() to prevent stale resolves.
   */
  _cancelPendingOut() {
    if (this._outResolve) {
      this._outResolve();
      this._outResolve = null;
    }
  }

  /**
   * Internal cleanup after "out" animation finishes naturally.
   * Hides spine, clears text, stops bone loop — identical end state to hide().
   */
  _finalizeOut() {
    if (!this.winlineSpine) return;

    this.stopBoneUpdateLoop();

    this.winlineSpine.alpha = 0.001;

    if (this.textBitmap) {
      this.textBitmap.text = "";
      this.textBitmap.alpha = 0.001;
    }
    if (this.textContainer) {
      this.textContainer.alpha = 0.001;
    }

    // Clear spine state so next showWinAmount() starts fresh
    if (this.winlineSpine.state) {
      this.winlineSpine.state.clearTracks();
      this.winlineSpine.state.clearListeners();
    }
  }

  /**
   * Hard kill — immediately hide at any stage (in/idle/out).
   * Used when user presses spin. Clears all state so next
   * showWinAmount() starts a fresh in→idle→out cycle.
   */
  hide() {
    if (!this.winlineSpine) return;

    // Resolve any pending playOut() promise immediately
    this._cancelPendingOut();

    // Cancel any pending show
    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }

    // Stop bone update loop
    this.stopBoneUpdateLoop();

    // Immediate alpha kill — no "out" animation
    this.winlineSpine.alpha = 0.001;

    // Clear text
    if (this.textBitmap) {
      this.textBitmap.text = "";
      this.textBitmap.alpha = 0.001;
    }
    if (this.textContainer) {
      this.textContainer.alpha = 0.001;
    }

    // Clean spine state — fully reset so next show starts fresh from "in"
    // NOTE: Do NOT call setToSetupPose() — causes DarkSlotMesh memory leak in spine-pixi-v7
    if (this.winlineSpine.state) {
      this.winlineSpine.state.clearTracks();
      this.winlineSpine.state.clearListeners();
      this.winlineSpine.state.timeScale = 1; // Reset speed-up for next use
    }
  }

  /**
   * Get total native duration of the animation chain (in→idle→out or idle→out).
   * Used to auto-compute timeScale for targetDuration.
   * @param {boolean} skipIntro - Whether "in" phase is skipped
   * @returns {number} Total duration in ms
   */
  _getAnimationChainDuration(skipIntro = false) {
    // Use pre-cached durations from SpineAnimationData (extracted once at init)
    const cached = SpineAnimationData.getWinTextChainDuration(skipIntro);
    if (cached > 0) return cached;

    // Fallback: read from live spine data if SpineAnimationData not yet initialized
    if (!this.winlineSpine?.skeleton?.data?.animations) return 0;

    const animations = this.winlineSpine.skeleton.data.animations;
    const getAnimDuration = (name) => {
      const anim = animations.find((a) => a.name === name);
      return anim ? anim.duration * 1000 : 0;
    };

    const inDuration = skipIntro ? 0 : getAnimDuration("in");
    const idleDuration = getAnimDuration("idle");
    const outDuration = getAnimDuration("out");

    return inDuration + idleDuration + outDuration;
  }

  /**
   * Update dimensions when symbol size changes
   */
  updateDimensions(newSymbolWidth, newSymbolHeight) {
    if (
      this.symbolWidth === newSymbolWidth &&
      this.symbolHeight === newSymbolHeight
    ) {
      return;
    }

    this.symbolWidth = newSymbolWidth;
    this.symbolHeight = newSymbolHeight;

    if (this.winlineSpine) {
      this.applySpineScaling();
    }
  }

  /**
   * Cleanup resources
   */
  cleanup() {
    this.isCleanedUp = true;

    // Cancel any pending show rAF
    if (this._showRAF) {
      cancelAnimationFrame(this._showRAF);
      this._showRAF = null;
    }

    // Stop bone update loop
    this.stopBoneUpdateLoop();

    // Cleanup text
    if (this.textBitmap) {
      this.textBitmap.destroy();
      this.textBitmap = null;
    }

    if (this.textContainer) {
      if (this.textContainer.parent) {
        this.textContainer.parent.removeChild(this.textContainer);
      }
      this.textContainer.destroy({ children: true });
      this.textContainer = null;
    }

    // Cleanup spine
    if (this.winlineSpine && !this.winlineSpine.destroyed) {
      try {
        if (this.winlineSpine.state) {
          this.winlineSpine.state.clearTracks();
          this.winlineSpine.state.clearListeners();
        }
        if (this.winlineSpine.parent) {
          this.winlineSpine.parent.removeChild(this.winlineSpine);
        }
        this.winlineSpine.destroy({
          children: true,
          texture: false,
          baseTexture: false,
        });
      } catch (error) {
        // Silent fail
      }
    }

    this.winlineSpine = null;
    this.container = null;
    this.initialized = false;
  }
}

export default WinTextController;
